import styled from '@emotion/styled';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import { Typography as Text } from '@eventbrite/marmalade';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Progress } from '../../shared';

export interface LoadingMessage {
    title: GenericLazyString;
    subtitle?: GenericLazyString;
    paragraph?: GenericLazyString;
}

export const messages: LoadingMessage[] = [
    {
        title: gettext('Thanks for giving us a spin!'),
        subtitle: gettext('Give us a moment to build your event...'),
    },
    {
        title: gettext('Scribbling down your event details...'),
    },
    {
        title: gettext(
            "Adding categories and tags to boost your event's visibility...",
        ),
    },
    {
        title: gettext(
            'Creating general admission tickets... (But you can create more!)',
        ),
    },
    {
        title: gettext('Crafting an event description...'),
    },
    {
        title: gettext("Adding an image to show your event's vibe..."),
    },
];

const publishingMessages: LoadingMessage[] = [
    {
        title: gettext('Publishing your event...'),
    },
    {
        title: gettext('Almost there...'),
    },
    {
        title: gettext('Just a few more seconds...'),
    },
];

export const DEFAULT_TIME_PER_MESSAGE_IN_MS = 5 * 1000;
export const TIME_PER_CHAR_IN_MS = 60;
export const TIME_OFFSET_FOR_ANIMATIONS_IN_AND_OUT_IN_MS = 1500;

export enum LoadingCarouselScreenTestId {
    Container = 'LoadingCarouselScreenContainer',
}

export const LoadingCarouselScreen = ({
    stage = 'transient-event',
}: {
    stage?: 'transient-event' | 'publish-event';
}) => {
    const [index, setIndex] = useState(0);

    const _messages =
        stage === 'transient-event' ? messages : publishingMessages;

    const pickMessage = useCallback(() => {
        let displayIndex = index + 1;
        if (displayIndex > _messages.length - 1) {
            displayIndex = 0;
        }
        setIndex(displayIndex);
    }, [index, _messages]);

    const calculatedMessageTime = useMemo(() => {
        const subtitleMessage = _messages[index].subtitle;
        const paragraphMessage = _messages[index].paragraph;
        let duration = DEFAULT_TIME_PER_MESSAGE_IN_MS;

        if (subtitleMessage) {
            duration = subtitleMessage.toString().length * TIME_PER_CHAR_IN_MS;
        }
        if (paragraphMessage) {
            duration = paragraphMessage.toString().length * TIME_PER_CHAR_IN_MS;
        }
        return duration;
    }, [index, _messages]);

    useEffect(() => {
        const initialTimer = setInterval(
            pickMessage,
            calculatedMessageTime + TIME_OFFSET_FOR_ANIMATIONS_IN_AND_OUT_IN_MS,
        );

        return () => clearInterval(initialTimer);
    }, [index, pickMessage, calculatedMessageTime]);

    return (
        <LoadingCarouselContainer
            data-testid={LoadingCarouselScreenTestId.Container}
        >
            <Spinner>
                <Progress />
            </Spinner>
            <Message
                key={_messages[index].title.toString()}
                messageDuration={calculatedMessageTime}
            >
                <Text as="h1" color="neutral-900" variant="heading-md">
                    {_messages[index].title}
                </Text>
                {_messages[index].subtitle && (
                    <Text as="h2" color="neutral-900" variant="heading-md">
                        {_messages[index].subtitle}
                    </Text>
                )}
                {_messages[index].paragraph && (
                    <Paragraph as="p" color="neutral-800" variant="heading-sm">
                        {_messages[index].paragraph}
                    </Paragraph>
                )}
            </Message>
        </LoadingCarouselContainer>
    );
};

const STYLES = {
    '$medium-narrow': '792px',
};

const Spinner = styled.div`
    margin-bottom: 2rem;
`;

const LoadingCarouselContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;

    max-width: 1024px;
    margin: 0 auto;
    padding: 0 4rem;

    @media (max-width: ${STYLES['$medium-narrow']}) {
        padding: 0 1rem;
    }

    height: 100vh;
`;

const Message = styled.div<{
    messageDuration: number;
}>`
    text-align: center;
    min-height: 120px;
    z-index: 1;
    position: relative;
    right: 0vw;
    opacity: 1;

    animation: message-slide-x-in 0.75s ease-in-out,
        ${({ messageDuration }) => messageDuration}ms 0.75s,
        message-slide-x-out 1s ease-in
            ${({ messageDuration }) => messageDuration + 750}ms;

    @keyframes message-slide-x-in {
        0% {
            right: -75vw;
            opacity: 0;
        }
        100% {
            opacity: 1;
            right: 0vw;
        }
    }

    @keyframes message-slide-x-out {
        0% {
            right: 0vw;
            opacity: 1;
        }

        75% {
            opacity: 0;
        }

        100% {
            right: 200vw;
        }
    }
`;

const Paragraph = styled(Text)`
    padding-top: 0.5rem;
`;
